import * as React from 'react'
import { LoaderIcon } from '@/app/features/icons/Loader'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryDriverBillingEvents } from '../api/useQueryBillingStats'
import type { BillingDriverEvent, BillingPeriod } from '../types'

// The shared `useFormatDate.formatDateWithTime` runs `.replace(/\./g, ':')`
// which corrupts ISO 8601 fractional seconds (`2026-05-19T06:52:10.580` →
// `…06:52:10:580`), so the date-fns v1 parser silently falls back to date-only
// at 00:00. Keep a local formatter rather than touching the shared hook to
// avoid regressing other call sites that may rely on its current quirks.
const formatLocalDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

interface Props {
  companyId: string
  driverId: number
  period: BillingPeriod
  /** Matches the parent table's total column count so the row spans full width. */
  colSpan: number
}

// Map server enum names to translation keys. Unknown values fall back to the
// raw enum string so misses are visible rather than hidden behind a placeholder.
const eventTypeKey = (t: (k: string) => string, eventType: string): string => {
  const key = `billing.eventType.${eventType.charAt(0).toLowerCase()}${eventType.slice(1)}`
  const translated = t(key)
  return translated === key ? eventType : translated
}

// The server sends the raw `ReportType` enum name (PascalCase). The printout
// module's own labels are keyed by the same name in camelCase
// (`TimesheetLumpSumSettlement` → `printout.timesheetLumpSumSettlement`), so
// derive the key rather than duplicating a ~250-entry table here. A report
// added on the server without a matching label falls back to its enum name.
const reportTypeLabel = (t: (k: string) => string, reportType: string): string => {
  const key = `printout.${reportType.charAt(0).toLowerCase()}${reportType.slice(1)}`
  const translated = t(key)
  return translated === key ? reportType : translated
}

// Second line of the accordion — "of what?" for each event. Printouts name the
// report, exports name the file, timesheet events name the artifact that was
// calculated, imports state how many activity days landed.
const eventDetails = (
  t: (k: string) => string,
  e: BillingDriverEvent,
): string => {
  switch (e.eventType) {
    case 'PrintoutGenerated':
      return e.reportType ? reportTypeLabel(t, e.reportType) : '—'
    case 'ExportGenerated':
      return e.fileName || '—'
    case 'TimesheetCalculated':
    case 'TimesheetRecalculated':
      return t('printout.timesheet')
    case 'TachographImported': {
      if (!e.dataPeriodStart || !e.dataPeriodEnd) return '—'
      const days = Math.round(
        (new Date(e.dataPeriodEnd).getTime() - new Date(e.dataPeriodStart).getTime())
          / 86400000,
      ) + 1
      return `${days} ${t('billing.activityDaysSuffix')}`
    }
    default:
      return '—'
  }
}

const formatPeriodSpan = (
  formatDate: (d: string | Date) => string,
  start: string | null,
  end: string | null,
): string => {
  if (!start && !end) return '-'
  if (start && end) {
    if (start === end) return formatDate(start)
    return `${formatDate(start)} – ${formatDate(end)}`
  }
  return formatDate((start || end) as string)
}

const BillingDriverEventsRow: React.FC<Props> = ({ companyId, driverId, period, colSpan }) => {
  const { t } = useLocalize()
  const { formatDate } = useFormatDate()
  const { data, isLoading, isError } = useQueryDriverBillingEvents(
    companyId,
    driverId,
    period,
    true,
  )

  // Plain <th> inherits app-wide header styling (uppercase + primary color)
  // which clashes with the parent table; the parent uses TableHeader with
  // explicit overrides. Hard-code the same look here so the nested table
  // reads as a subordinate continuation of its parent.
  const thClass =
    'tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-content-base tw-opacity-80'

  return (
    <tr className="tw-border-b-2 tw-border-b-gray-300 tw-border-t tw-border-t-gray-200">
      <td colSpan={colSpan} className="tw-bg-[#F2F2F2]/40 tw-py-0 tw-pl-0 tw-pr-2 tw-shadow-inner">
        <div className="tw-flex">
          <div className="tw-w-1 tw-flex-shrink-0 tw-bg-primary/30" />
          <div className="tw-flex-1">
            {isLoading ? (
              <div className="tw-grid tw-place-items-center tw-py-6">
                <LoaderIcon className="tw-w-10 tw-animate-spin" />
              </div>
            ) : isError ? (
              <div className="tw-px-4 tw-py-4 tw-text-sm tw-text-red-700">
                {t('billing.eventsLoadError')}
              </div>
            ) : (data?.length ?? 0) === 0 ? (
              <div className="tw-px-4 tw-py-4 tw-text-sm tw-text-content-secondary">
                {t('billing.noEventsInPeriod')}
              </div>
            ) : (
              <table className="tw-w-full tw-table-auto tw-text-sm">
                <thead>
                  <tr>
                    <th className={thClass}>{t('billing.col.eventType')}</th>
                    <th className={thClass}>{t('billing.col.details')}</th>
                    <th className={thClass}>{t('billing.col.occurredAt')}</th>
                    <th className={thClass}>{t('billing.col.dataPeriod')}</th>
                    <th className={thClass}>{t('billing.col.source')}</th>
                    <th className={thClass}>{t('billing.col.triggeredBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.map((e: BillingDriverEvent) => {
                    const displayName = e.triggeredByUserName
                      ?? e.triggeredByUserEmail
                      ?? e.triggeredByUserId
                      ?? '—'
                    const tooltip = e.triggeredByUserEmail
                      && e.triggeredByUserName
                      && e.triggeredByUserEmail !== e.triggeredByUserName
                      ? e.triggeredByUserEmail
                      : undefined
                    return (
                      <tr key={e.id} className="tw-border-t tw-border-t-gray-200">
                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">{eventTypeKey(t, e.eventType)}</td>
                        <td className="tw-px-3 tw-py-2">{eventDetails(t, e)}</td>
                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">{formatLocalDateTime(e.occurredAt)}</td>
                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                          {formatPeriodSpan(formatDate, e.dataPeriodStart, e.dataPeriodEnd)}
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-text-content-secondary">{e.source.toUpperCase()}</td>
                        <td className="tw-px-3 tw-py-2" title={tooltip}>{displayName}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </td>
    </tr>
  )
}

export default BillingDriverEventsRow
